import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { BehaviorSubject, Subject } from 'rxjs';
import { DEFAULT_DATE_FORMAT, MM_DD_YYYY } from '../constants/constants';
import { getAuditTypeString } from '../helpers/helpers';
import { ActivityTypesEnum } from '../interfaces/ActivityTypesEnum';
import { AuditTypesEnum } from '../interfaces/IAudit';
import { IValidatedTransactionDate, TransactionDateError } from '../interfaces/IValidatedTransactionDate';
import { TransactionSearchCriteria } from '../interfaces/search-criteria';
import { FormattedDatePipe } from '../pipes/formatted-date/formatted-date.pipe';
import { RxNumberPipe } from '../pipes/rx-number/rx-number.pipe';
import { AlertMessageService } from './alert-message.service';
import { PharmacyState } from '../states/pharmacy/pharmacy.state';
//Don't provide it anywhere
@Injectable({
  providedIn: 'root',
})
export class TransactionsService {
  widgetTransaction$: Subject<string> = new Subject<string>();
  transactionsFilter = new TransactionSearchCriteria();
  transactionSuccessfullyAmended$ = new Subject();
  openReceivingDialogRequested$ = new Subject();
  transactionDateSuccessfullyUpdated$ = new Subject();
  transactionDateErrorMessage$ = new BehaviorSubject<string>(null);
  refreshTransactionsTable$ = new Subject();
  pendingTransactionModified$ = new Subject<number>();

  private rxNumberPipe: RxNumberPipe;

  constructor(private alert: AlertMessageService, private formattedDate: FormattedDatePipe, private store: Store) {
    this.rxNumberPipe = new RxNumberPipe(store);  }

  handleTransactionDateErrors(errors: IValidatedTransactionDate | IValidatedTransactionDate[]): void {
    errors = Array.isArray(errors) ? errors : [errors];
    if(!errors)
      return;

    const error = {content: '', type: 'error-info', autoHide: 10, closeButtonName: null, width: 'fit-content'};
    if (errors[0]?.transactionDateError === TransactionDateError.transactionDateBeyondLastAudit) {
      const auditTime = errors[0]?.lastAuditedOn ? this.formattedDate.transform(errors[0].lastAuditedOn, DEFAULT_DATE_FORMAT) : '';
      const auditType = errors[0]?.auditType ?? '';
      const ndc = errors[0]?.packageNdc ?? '';
      error.content = `Must pick a date after ${(AuditTypesEnum[auditType])?.toString()?.replace(/([a-z])([A-Z])/g, '$1 $2')} audit of ${ndc} on ${auditTime}.`;
    } else if (errors[0]?.transactionDateError === TransactionDateError.amendBeforeOriginalTransaction) {
      const newDate = errors[0]?.newValidTransactionDate ? this.formattedDate.transform(errors[0]?.newValidTransactionDate, DEFAULT_DATE_FORMAT) : '';
      error.content = `Amend Date and Time must be after ${newDate}.`;
    } else if (errors[0]?.transactionDateError === TransactionDateError.negativeBalance) {
      const newValidTrDate = this.formattedDate.transform(errors[0]?.newValidTransactionDate, DEFAULT_DATE_FORMAT);
      const availableQty = errors[0].newTransactionDateAvailableQty;
      if (newValidTrDate && availableQty) {
        error.content = `Insufficient inventory. There is ${availableQty} available as of ${newValidTrDate}.`;
      }
      else {
        error.content = `Insufficient inventory for the selected Date and Time.`;
      }
    }  else if (errors[0]?.transactionDateError === TransactionDateError.movedToOlderThanMovedFrom) {
      const newValidTrDate = this.formattedDate.transform(errors[0]?.newValidTransactionDate, DEFAULT_DATE_FORMAT);
      error.content = `Moved From Date and Time must be before ${newValidTrDate}`;
    } else if (errors[0]?.transactionDateError === TransactionDateError.movedFromYoungerThanMovedTo) {
      const newValidTrDate = this.formattedDate.transform(errors[0]?.newValidTransactionDate, DEFAULT_DATE_FORMAT);
      error.content = `Moved To Date and Time must be after ${newValidTrDate}`;
    }

    if(error.content) {
      this.alert.alertMessage$.next(error);
    }
  }

  mapTransactions(transactions: any[]) {
    const currentPharmacyName = this.store.selectSnapshot(PharmacyState.currentPharmacy)?.name;
    return transactions.map(transaction => {
      switch (transaction?.activityType) {
        case ActivityTypesEnum.Dispensed:
          return {...transaction, infoHeading: transaction?.rxNumber ? 'Rx - ' + this.rxNumberPipe.transform(transaction) : '', infoSecondary: (transaction?.patientName as string)?.toUpperCase()};
        case  ActivityTypesEnum.Amendment: {
         return transaction?.rxNumber ? 
          {...transaction, infoHeading: transaction?.rxNumber ? 'Rx - ' + this.rxNumberPipe.transform(transaction) : '', infoSecondary: (transaction?.patientName as string)?.toUpperCase(), infoSecondary2: !transaction?.amendReasonOtherDescription ? transaction?.amendReason : transaction?.amendReasonOtherDescription} :
          {...transaction, infoHeading: !transaction?.amendReasonOtherDescription ? transaction?.amendReason : transaction?.amendReasonOtherDescription};
        }
        case ActivityTypesEnum.LostStolen, ActivityTypesEnum.DamagedExpired, ActivityTypesEnum.ReturnedToWholesaler, ActivityTypesEnum.Sold:
          return {...transaction, infoHeading: transaction?.description, infoSecondary: ''};
        case ActivityTypesEnum.Received:
          return {...transaction, infoHeading: transaction?.isOrderLineItem ? transaction?.orderSupplierName || transaction?.orderWholesalerName : '', infoSecondary: transaction?.orderInvoiceNumber};
        case ActivityTypesEnum.Audit:
          return {...transaction, infoHeading: getAuditTypeString(transaction.auditType), infoSecondary: transaction?.auditConfirmationDate? this.formattedDate.transform(transaction.auditConfirmationDate, DEFAULT_DATE_FORMAT) : ''};
        case ActivityTypesEnum.SoldToProvider:
          return {...transaction, infoHeading: transaction?.orderProviderName || transaction?.orderBuyerName, infoSecondary: transaction?.orderPurchaseOrder};
        case ActivityTypesEnum.SoldToReverseDistributor:
          return {...transaction, infoHeading: transaction?.orderReverseDistributorName || transaction?.orderBuyerName, infoSecondary: transaction?.orderPurchaseOrder};
        case ActivityTypesEnum.Returned:
          return {...transaction, infoHeading: transaction?.orderWholesalerName || transaction?.orderBuyerName, infoSecondary: transaction?.orderMRA};
        case ActivityTypesEnum.WastedDestroyed:
          return {...transaction, infoHeading: transaction?.orderNameOfDestruction, infoSecondary: transaction?.orderPlaceOfDestructionName ?? transaction?.orderInvoiceNumber};
        case ActivityTypesEnum.Compounded:
          return {...transaction, infoHeading: transaction?.lot, infoSecondary: this.formattedDate.transform(transaction?.expirationDate, MM_DD_YYYY)};
        default: 
          return {...transaction, infoHeading: '', infoSecondary: ''};
      }
    });
  }
}
